import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import Icon from '../components/Icon';
import Sheet from '../components/Sheet';
import { PlanWeekSkeleton } from '../components/Skeletons';
import PlanMealCard from '../components/PlanMealCard';
import ShoppingList from '../components/ShoppingList';
import { PRIMARY } from '../components/CaptureSheets';
import { ProBadge } from '../components/ProSheet';
import { WEEKDAY_SHORT, addDays, longDate, parseISODate, today } from '../lib/dates';
import { MEAL_LABELS, grams, kcal } from '../lib/format';
import { isPlannedMealLogged, logPlannedMeal, planAverages, planDates, planRange, planStatus } from '../lib/plans';
import { useApp, useAppPath } from '../lib/AppContext';
import DateField from '../components/DateField';
import { NewBadge, useAnnouncementAnchor } from '../components/Announcements';
import { useCapturedDay } from '../components/Capture';
import ArrangeWeek from '../components/ArrangeWeek';
import { TakeoutPrompt, TakeoutSheet, useTakeouts } from '../components/Takeout';
import usePair from '../cook/usePair';
import PairManager from '../cook/PairManager';
import InviteSheet from '../cook/InviteSheet';
import PairShopping from '../cook/PairShopping';
import { ActivityLines, CookBanner, OwnMealsCard } from '../cook/PairCards';
import { SourceLine } from '../components/DataSource';

const TABS = [
  { id: 'menu', label: 'Thực đơn' },
  { id: 'shopping', label: 'Đi chợ' },
  { id: 'tips', label: 'Lời khuyên' },
];
const ROW = 'flex min-h-14 w-full items-center gap-3 rounded-[16px] border border-line px-4 text-left font-semibold';

// This week's wishes and start date are saved as they are; the menu isn't rebuilt.
function RequestForm({ plan, onSave }) {
  const [weekStart, setWeekStart] = useState(plan.week_start);
  const [wishes, setWishes] = useState(plan.preferences || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave({ week_start: weekStart, preferences: wishes });
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label htmlFor="plan-week-start" className="text-[15px] font-semibold">Tuần bắt đầu từ</label>
        <DateField id="plan-week-start" title="Tuần bắt đầu từ" weekday value={weekStart} onChange={setWeekStart} />
      </div>
      <label className="flex flex-col gap-2">
        <span className="text-[15px] font-semibold">Mong muốn tuần này</span>
        <textarea className="textarea min-h-24 w-full rounded-[14px] border-line text-base" value={wishes}
          placeholder="VD: ăn nhiều cá hơn" onChange={(e) => setWishes(e.target.value)} />
      </label>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <div className="flex flex-col items-center gap-2">
        <button type="submit" className={PRIMARY} disabled={busy}>Lưu</button>
        <p className="text-xs text-muted">Thay đổi ở đây không tạo lại thực đơn</p>
      </div>
    </form>
  );
}

// The menu remembers the targets it was built for; after the user changes targets it says so and offers a
// rebuild (their choice: it costs AI credits). Menus from before targets were remembered show nothing.
function TargetsChangedNote({ targets, goals, onRebuild }) {
  if (!targets) return null;
  const changed = (targets.calories ?? null) !== (goals.calories ?? null)
    || (targets.protein_rest_g ?? null) !== (goals.protein_g ?? null)
    || (targets.protein_training_g ?? null) !== (goals.workout_protein_g ?? goals.protein_g ?? null);
  if (!changed) return null;
  const describe = (kcalValue, protein) => [kcalValue && `${kcal(kcalValue)} kcal`, protein && `${grams(protein)} đạm`].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-col gap-2 rounded-[16px] bg-[#fdf3d4] p-4 text-sm text-[#5c4300]">
      <p>
        Thực đơn này được lập theo mục tiêu cũ ({describe(targets.calories, targets.protein_rest_g)}).
        Mục tiêu hiện tại: {describe(goals.calories, goals.protein_g)}.
      </p>
      <button type="button" className="btn h-11 w-fit rounded-[12px] border-0 bg-[#17201b] px-4 text-sm font-bold text-white" onClick={onRebuild}>
        <Icon name="refresh" className="size-4" /> Tạo lại theo mục tiêu mới
      </button>
    </div>
  );
}

// "⋯": Chỉnh sửa yêu cầu, Tạo lại and Xóa thực đơn, the last two with a confirm step.
function OptionsSheet({ open, initialConfirm = null, onClose, onEditRequest, onRegenerate, onDelete, onCook }) {
  const { membership, openPaywall } = useApp();
  const locked = membership && !membership.pro;
  const cookAnchor = useAnnouncementAnchor('plan_cook_row');
  const [confirm, setConfirm] = useState(null);
  // Opened from the "built for older targets" note: straight to the rebuild confirmation.
  useEffect(() => { if (open) setConfirm(initialConfirm); }, [open, initialConfirm]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const close = () => { setConfirm(null); setError(null); onClose(); };

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setConfirm(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const title = { regenerate: 'Tạo lại thực đơn?', delete: 'Xóa thực đơn này?' }[confirm] || 'Tùy chọn';
  return (
    <Sheet open={open} onClose={close} title={title}>
      {!confirm && (
        <div className="flex flex-col gap-2">
          {onCook && (
            <button ref={cookAnchor} type="button" className={ROW} onClick={onCook}>
              <Icon name="bowl" className="size-5" /> Nấu chung <NewBadge anchor="plan_cook_row" />
            </button>
          )}
          <button type="button" className={ROW} onClick={onEditRequest}><Icon name="pencil" className="size-5" /> Chỉnh sửa yêu cầu</button>
          <button type="button" className={ROW}
            onClick={() => (locked ? (onClose(), openPaywall('pro_required')) : setConfirm('regenerate'))}>
            <Icon name="refresh" className="size-5" /> Tạo lại{locked && <ProBadge />}
          </button>
          <button type="button" className={`${ROW} text-error`} onClick={() => setConfirm('delete')}><Icon name="trash" className="size-5" /> Xóa thực đơn</button>
        </div>
      )}
      {confirm === 'regenerate' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm">AI lên lại cả tuần theo mục tiêu và hồ sơ hiện tại. Các món bạn đã sửa và dấu đã mua sẽ mất.</p>
          <button type="button" className={PRIMARY} disabled={busy} onClick={() => run(onRegenerate)}>
            {busy ? <><span className="loading loading-spinner loading-sm" /> AI đang lên lại thực đơn…</> : 'Tạo lại thực đơn'}
          </button>
          <button type="button" className="btn btn-ghost h-11 rounded-2xl" disabled={busy} onClick={() => setConfirm(null)}>Hủy</button>
        </div>
      )}
      {confirm === 'delete' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm">Thực đơn và danh sách đi chợ của tuần này sẽ bị xóa. Các bữa đã ghi vào Hôm nay vẫn giữ nguyên.</p>
          <button type="button" className="btn btn-error h-[54px] w-full rounded-2xl border-0 text-base font-bold" disabled={busy}
            onClick={() => run(onDelete)}>
            Xóa thực đơn
          </button>
          <button type="button" className="btn btn-ghost h-11 rounded-2xl" disabled={busy} onClick={() => setConfirm(null)}>Hủy</button>
        </div>
      )}
      {error && <p role="alert" className="mt-3 text-sm font-medium text-error">{error}</p>}
    </Sheet>
  );
}

// Dishes ordered out instead of cooked: their ingredients can come off the shopping list. The list is the
// whole week's, added up, so it can't be trimmed exactly; this names what's no longer needed.
function OrderedOutNote({ days, dates }) {
  const ordered = days.flatMap((day, i) => day.meals.filter((m) => m.takeout).map((m) => ({ date: dates[i], meal: m })));
  if (!ordered.length) return null;
  return (
    <div className="flex flex-col gap-2 rounded-[16px] bg-[#fdf3d4] p-4 text-sm text-[#5c4300]">
      <p className="font-semibold">Đã đặt ngoài, có thể bớt khi đi chợ:</p>
      <ul className="flex flex-col gap-1.5">
        {ordered.map(({ date, meal }) => (
          <li key={`${date}-${meal.meal_type}-${meal.name}`}>
            <b>{WEEKDAY_SHORT[(parseISODate(date).getDay() + 6) % 7]} · {MEAL_LABELS[meal.meal_type]}:</b> {meal.takeout.original.description || meal.takeout.original.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

// One week's menu: summary chips, then Thực đơn (day by day), Đi chợ and Lời khuyên.
export default function PlanEditPage() {
  const { id } = useParams();
  const { api, editable, goals, membership, openPaywall } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  // The log of each day looked at (today and past days), to show which meals were eaten.
  const [logs, setLogs] = useState({});
  const setDayLog = useCallback((log) => setLogs((all) => ({ ...all, [log.date]: log })), []);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sheet, setSheet] = useState(null); // null | 'options' | 'request'
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'menu';
  const optionsAnchor = useAnnouncementAnchor('plan_week_options');
  const arrangeAnchor = useAnnouncementAnchor('plan_arrange');
  // Sắp xếp tuần: null, or open with an optional dish already lifted ({ day, meal } indexes).
  const [arranging, setArranging] = useState(null);
  const takeoutAnchor = useAnnouncementAnchor('plan_takeout');
  // Đặt ngoài: the open suggestion sheet's request, and today's "Bạn đã đặt món gì?" questions.
  const [takeoutRequest, setTakeoutRequest] = useState(null);
  const takeouts = useTakeouts(today(), { onPlan: setPlan, onDay: setDayLog });
  const todayIso = today();
  // Nấu chung: the pair (avatar pair, banner ①, invite sheet). A Free member sees the shared menu read-only.
  const [pairState, setPairState, reloadPair] = usePair();
  const [inviting, setInviting] = useState(false);
  const [slotsOpen, setSlotsOpen] = useState(false);
  const pair = pairState?.pair;
  const canEdit = editable && !(pair && !pair.can_edit);

  const load = useCallback(() => api.get(`/meal_plans/${id}`).then(setPlan).catch((e) => setError(e.message)), [api, id]);
  useEffect(() => { load(); }, [load]);

  const dates = plan ? planDates(plan) : [];
  const coversToday = dates.includes(todayIso);
  const todayLog = logs[todayIso] || null;
  // The day shown, and today (Sắp xếp needs it): a meal logged in a slot marks that slot eaten.
  const viewDate = plan ? dates[selected ?? Math.max(0, dates.indexOf(todayIso))] : null;
  useEffect(() => {
    [viewDate, coversToday ? todayIso : null].filter((d) => d && d <= todayIso && !logs[d])
      .forEach((d) => api.get(`/days/${d}`).then(setDayLog).catch(() => {}));
  }, [api, viewDate, coversToday, todayIso, setDayLog]); // eslint-disable-line react-hooks/exhaustive-deps
  // A meal logged with the camera from here updates the cards in place.
  useCapturedDay(todayIso, setDayLog);

  if (error) return <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>;
  if (!plan) return <PlanWeekSkeleton />;

  const { days = [], shopping_list: shopping = [], tips = [], focus } = plan.plan;
  const status = planStatus(plan);
  const avg = planAverages(plan);
  const dayIndex = selected ?? Math.max(0, dates.indexOf(todayIso));
  const day = days[dayIndex];
  const dayDate = dates[dayIndex];
  const dayLog = dayDate <= todayIso ? logs[dayDate] : null;

  // Only weeks that still have days ahead can be rearranged (past days stay as they were).
  const canArrange = canEdit && status.kind !== 'past';
  // The banner shows on this week's and next week's menu only.
  const bannerWeek = status.kind !== 'past' && dates[0] <= addDays(todayIso, 13);
  const setTab = (next) => setParams(next === 'menu' ? {} : { tab: next }, { replace: true });
  // All plan endpoints return the whole updated menu (day totals and shopping list included).
  const update = async (method, suffix, body) => setPlan(await api[method](`/meal_plans/${id}${suffix}`, body));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Link to={path('/plan')} className="flex min-h-11 items-center gap-1 font-semibold">
          <Icon name="chevronLeft" className="size-5" /> Tất cả thực đơn
        </Link>
        {editable && (
          <div className="flex items-center gap-2">
            <PairManager state={pairState} setState={setPairState} onChanged={load} slotsOpen={slotsOpen} setSlotsOpen={setSlotsOpen} />
            {canArrange && !pair && (
              <button ref={arrangeAnchor} type="button" onClick={() => setArranging({})}
                className="btn h-11 rounded-full border-2 border-primary bg-base-100 px-4 font-bold text-brand-text">
                <Icon name="arrange" className="size-4" /> Sắp xếp
              </button>
            )}
            {canArrange && pair && (
              <button ref={arrangeAnchor} type="button" onClick={() => setArranging({})} aria-label="Sắp xếp"
                className="grid size-11 place-items-center rounded-full border-2 border-primary bg-base-100 text-brand-text">
                <Icon name="arrange" className="size-4" />
              </button>
            )}
            {canEdit && <button ref={optionsAnchor} type="button" className="relative grid size-11 place-items-center rounded-full border border-line bg-base-100"
              aria-label="Tùy chọn thực đơn" onClick={() => setSheet('options')}>
              <Icon name="dotsH" className="size-6" />
              <NewBadge anchor="plan_week_options" dot />
            </button>}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="tabular text-[26px] font-extrabold leading-tight">{planRange(plan)}</h1>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${status.chip}`}>{status.short}</span>
        </div>
        <div className="flex flex-wrap gap-2 text-sm font-semibold">
          <span className="tabular rounded-full border border-line bg-base-100 px-3 py-1.5">≈ {kcal(avg.calories)} kcal/ngày</span>
          <span className="tabular rounded-full bg-[#e6eefc] px-3 py-1.5 text-[#2456b0]">{grams(avg.protein)} đạm/ngày</span>
          {focus && <span className="rounded-full border border-line bg-base-100 px-3 py-1.5">{focus}</span>}
        </div>
        {editable && bannerWeek && (
          <CookBanner state={pairState} weekStart={plan.week_start} onInvite={() => setInviting(true)}
            onDismiss={async () => setPairState(await api.post('/cooking_pair/banner', { week_start: plan.week_start }))} />
        )}
        {editable && pair && plan.own_missing && status.kind !== 'past' && <OwnMealsCard planId={plan.id} canEdit={pair.can_edit} onDone={setPlan} />}
        {canEdit && status.kind !== 'past' && !pair && <TargetsChangedNote targets={plan.plan.targets} goals={goals}
          onRebuild={() => (membership && !membership.pro ? openPaywall('pro_required') : setSheet('rebuild'))} />}
        {canEdit && (
          <button type="button" className="flex min-h-11 w-fit items-center gap-2 text-sm font-semibold text-brand-text"
            onClick={() => setSheet('request')}>
            <Icon name="pencil" className="size-4" /> Chỉnh sửa yêu cầu tuần này
          </button>
        )}
      </div>

      <div role="tablist" aria-label="Phần của thực đơn" className="grid grid-cols-3 gap-1 rounded-[16px] bg-base-300/70 p-1">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`h-11 rounded-[12px] text-[15px] font-semibold ${tab === t.id ? 'bg-base-100 shadow-sm' : 'text-muted'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'menu' && (
        <div role="tabpanel" className="flex flex-col gap-4">
          <div className="grid grid-cols-7 gap-1.5">
            {dates.map((iso, i) => (
              <button key={iso} type="button" onClick={() => setSelected(i)} aria-pressed={i === dayIndex}
                aria-current={iso === todayIso ? 'date' : undefined}
                aria-label={longDate(iso)}
                className={`flex h-[58px] flex-col items-center justify-center rounded-[14px] border
                  ${i === dayIndex ? 'border-neutral bg-neutral text-neutral-content' : 'border-line bg-base-100'}
                  ${iso === todayIso && i !== dayIndex ? 'border-primary' : ''}`}>
                <span className={`text-[11px] ${i === dayIndex ? 'text-neutral-content/80' : 'text-muted'}`}>
                  {WEEKDAY_SHORT[(parseISODate(iso).getDay() + 6) % 7]}
                </span>
                <span className="tabular text-[17px] font-bold leading-none">{parseISODate(iso).getDate()}</span>
              </button>
            ))}
          </div>

          {day ? (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <h2 className="text-lg font-bold">{longDate(dayDate)}</h2>
                <span className="tabular text-sm">
                  {day.meals.some((m) => m.takeout) && '≈ '}<b>{kcal(day.total_calories)}</b> kcal · <b className="text-protein">{grams(day.total_protein_g)}</b> đạm
                </span>
              </div>
              <SourceLine items={day.meals.flatMap((m) => m.ingredients || [])} className="-mt-1" />
              {dayLog?.meals.length > 0 && (
                <p className="tabular -mt-2 text-sm text-muted">
                  Đã ăn <b className="text-base-content">{kcal(dayLog.totals.calories_in)}</b> / {kcal(day.total_calories)} kcal theo thực đơn
                </p>
              )}
              {pair && <ActivityLines lines={day.activities} onUndo={async (line) => setPlan(await api.post(`/pair_activities/${line.id}/undo`))} />}
              {dayDate === todayIso && takeouts.open.map((t) => <TakeoutPrompt key={t.id} takeout={t} onResolve={takeouts.resolve} />)}
              {day.meals.map((meal, j) => (
                <PlanMealCard key={`${dayIndex}-${j}`} meal={meal} readOnly={!canEdit}
                  canLog={editable && dayDate === todayIso} logged={isPlannedMealLogged(dayLog, meal)}
                  eaten={(dayLog?.meals || []).filter((m) => m.meal_type === meal.meal_type)} today={dayDate === todayIso}
                  onLog={async () => setDayLog(await logPlannedMeal(api, dayDate, meal.pair?.totals
                    ? { ...meal, calories: meal.pair.totals.me.calories, protein_g: meal.pair.totals.me.protein_g } : meal))}
                  onSwap={(reason, avoid) => update('post', '/replace_meal', { day_index: dayIndex, meal_index: j, reason, avoid })}
                  onSave={(updated) => update('patch', '/update_meal', { day_index: dayIndex, meal_index: j, meal: updated })}
                  onMove={canArrange && dayDate >= todayIso ? () => setArranging({ day: dayIndex, meal: j }) : null}
                  takeoutRef={j === 0 ? takeoutAnchor : undefined}
                  onTakeout={canEdit && dayDate === todayIso
                    ? () => setTakeoutRequest({ date: dayDate, mealType: meal.meal_type, mealPlanId: plan.id, dayIndex, mealIndex: j }) : null} />
              ))}
            </>
          ) : (
            <p className="rounded-[20px] bg-base-100 p-5 text-center text-sm text-muted">Ngày này chưa có món nào.</p>
          )}
        </div>
      )}

      {tab === 'shopping' && (
        <div role="tabpanel" className="flex flex-col gap-3">
          <OrderedOutNote days={days} dates={dates} />
          {pair ? <PairShopping weekStart={plan.week_start} partnerName={pair.partner.name} /> : (
            <ShoppingList title={`Đi chợ tuần ${planRange(plan)}`} items={shopping}
              onToggle={(index, checked) => update('patch', '/shopping_item', { index, checked })} />
          )}
        </div>
      )}

      {tab === 'tips' && (
        <ul role="tabpanel" className="flex flex-col divide-y divide-line rounded-[20px] bg-base-100 px-4">
          {tips.length === 0 && <li className="py-4 text-center text-sm text-muted">Chưa có lời khuyên nào.</li>}
          {tips.map((tip) => (
            <li key={tip} className="flex gap-3 py-3 text-[15px]">
              <Icon name="sparkles" className="mt-0.5 size-5 shrink-0 text-brand-text" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      )}

      <TakeoutSheet request={takeoutRequest} onClose={() => setTakeoutRequest(null)} onOrdered={takeouts.added} />
      {arranging && (
        <ArrangeWeek plan={plan} todayLog={todayLog} goal={plan.plan.targets?.calories ?? goals.calories}
          initialPick={arranging.day != null ? arranging : null} onSaved={setPlan} onClose={() => setArranging(null)} />
      )}
      <OptionsSheet open={sheet === 'options' || sheet === 'rebuild'} initialConfirm={sheet === 'rebuild' ? 'regenerate' : null}
        onClose={() => setSheet(null)}
        onEditRequest={() => setSheet('request')}
        onCook={!pair && pairState ? () => { setSheet(null); setInviting(true); } : null}
        onRegenerate={async () => { await update('post', '/regenerate'); setSheet(null); }}
        onDelete={async () => { await api.del(`/meal_plans/${id}`); navigate(path('/plan'), { replace: true }); }} />
      <InviteSheet open={inviting} onClose={() => { setInviting(false); reloadPair(); }} />
      <Sheet open={sheet === 'request'} onClose={() => setSheet(null)} title="Chỉnh sửa yêu cầu">
        <RequestForm plan={plan} onSave={async (attrs) => { await update('patch', '', attrs); setSheet(null); }} />
      </Sheet>
    </div>
  );
}
